import React from 'react';
import { configureStore } from '@reduxjs/toolkit';

// saga
import { Provider, useDispatch } from 'react-redux';
import createSagaMiddleware from 'redux-saga';

// router
import { Router } from 'react-router-dom';

import { reducer, rootSaga } from './store/index';
import apiSequencerMiddleware from './store/apiSequencerMiddleware';
import persistentStateMiddleware from './store/persistentStateMiddleware';
import openBankingMiddleware from './store/openBanking/openBankingMiddleware';
import {
  navigationMiddleware,
  history,
} from './middleware/navigationMiddleware';
import analyticsMiddleware from './middleware/analyticsMiddleware';

const sagaMiddleware = createSagaMiddleware();
const middleware = [
  navigationMiddleware,
  sagaMiddleware,
  apiSequencerMiddleware,
  persistentStateMiddleware,
  analyticsMiddleware,
  openBankingMiddleware,
];

const mcx_test_tools_enhancer = window.__MCX_REDUX_TOOLS__?.('mym');

const customEnhancers = mcx_test_tools_enhancer
  ? [mcx_test_tools_enhancer]
  : [];

const store = configureStore({
  reducer: reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(middleware),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(customEnhancers),
  devTools: { name: 'My Mortgage' },
});

export type AppStore = typeof store;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();

sagaMiddleware.run(rootSaga);

export default function withRedux<T extends object>(
  WrappedComponent: React.ComponentType<T>
) {
  return class WithRedux extends React.Component<T> {
    render() {
      return (
        <Provider store={store}>
          <Router history={history}>
            <WrappedComponent {...this.props} />
          </Router>
        </Provider>
      );
    }
  };
}
